/* Common types */

export enum Timescales {
    live = 'live',
    hourly = 'hau',
    daily = 'dau',
    weekly = 'wau',
    monthly = 'mau'
}

export enum PerfSource {
    hourly = 'perf_v2',
    daily = 'perf_daily_v2'
}

export type SinceOptions =
    | 'all'
    | '5m'
    | '15m'
    | '30m'
    | '1h'
    | '3h'
    | '6h'
    | '12h'
    | '24h'
    | '2d'
    | '7d'
    | '30d'
    | '90d';

export interface BaseDataItem {
    creative_island_code: string;
}

/* Total Active Users */

export interface LiveDataItem extends BaseDataItem {
    timestamp: string;
    ccu: number;
}

export interface DailyDataItem extends BaseDataItem {
    day: string;
    unique_users: number;
}

export interface WeeklyDataItem extends BaseDataItem {
    week: string;
    unique_users: number;
}

export interface MonthlyDataItem extends BaseDataItem {
    month: string;
    unique_users: number;
}

export interface DefaultParams {
    since?: SinceOptions;
    islandCode?: string;
    fromTs?: number;
    toTs?: number;
    topN?: number;
    mostRecent?: number;
}

/* Peak and Avg */

export interface PeakDataItem extends BaseDataItem {
    peak_ccu: number;
}
export interface AvgDataItem extends BaseDataItem {
    avg_ccu: number;
}

export type DataItemType<T> = T extends Timescales.live
    ? LiveDataItem
    : T extends Timescales.daily
    ? DailyDataItem
    : T extends Timescales.weekly
    ? WeeklyDataItem
    : T extends Timescales.monthly
    ? MonthlyDataItem
    : never;

/* Comparison  */

export interface ComparisonBaseDataItem extends BaseDataItem {
    unique_users: number;
    previous_unique_users: number;
    diff: number;
}

export interface ComparisonDailyDataItem extends ComparisonBaseDataItem {
    day: string;
    previous_day: number;
}

export interface ComparisonWeeklyDataItem extends ComparisonBaseDataItem {
    week: string;
    previous_week: number;
}

export interface ComparisonMonthlyDataItem extends ComparisonBaseDataItem {
    month: string;
    previous_month: number;
}

export type ComparisonDataItemType<T> = T extends Timescales.daily
    ? ComparisonDailyDataItem
    : T extends Timescales.weekly
    ? ComparisonWeeklyDataItem
    : T extends Timescales.monthly
    ? ComparisonMonthlyDataItem
    : never;

/* Top Islands */

export interface TopIslandsDataItem extends BaseDataItem {
    rank: number;
}

/* Play Time */

export enum PlaytimeTimescales {
    daily = 'dau',
    weekly = 'wau',
    monthly = 'mau'
}

export type Playtime = 'avg';

export interface PlaytimeParams extends DefaultParams {
    playtime?: Playtime;
}

export interface DailyPlaytimeDataItem extends BaseDataItem {
    day: string;
    playtime: number;
}

export interface WeeklyPlaytimeDataItem extends BaseDataItem {
    week: string;
    playtime: number;
}

export interface MonthlyPlaytimeDataItem extends BaseDataItem {
    month: string;
    playtime: number;
}

export type PlaytimeDataItemType<T> = T extends PlaytimeTimescales.daily
    ? DailyPlaytimeDataItem
    : T extends PlaytimeTimescales.weekly
    ? WeeklyPlaytimeDataItem
    : T extends PlaytimeTimescales.monthly
    ? MonthlyPlaytimeDataItem
    : never;

/* Retention */
export interface RetentionDataItem extends BaseDataItem {
    day: string;
    retention: number;
    retained_dau: number;
}

export interface ExternalTotalsResponseDataItem extends BaseDataItem {
    source: 'totals_v1';
    timestamp: string;
    payload: string;
}

export interface ExternalTotalsDataItem extends BaseDataItem {
    source: 'totals_v1';
    timestamp: string;
    payload: {total_plays: number; unique_users: number};
}

export interface ExternalTotalsResponseDataItem extends BaseDataItem {
    source: 'totals_v1';
    timestamp: string;
    payload: string;
}

export interface ExternalTotalsDataItem extends BaseDataItem {
    source: 'totals_v1';
    timestamp: string;
    payload: {total_plays: number; unique_users: number};
}

/* External totals  (Total Plays/Unique users)*/

export interface ExternalTotalsParams extends DefaultParams {
    mostRecent?: number;
}

export interface PayoutResponseDataItem extends BaseDataItem {
    source: 'earnings_v1';
    timestamp: string;
    payload: string;
}

export interface BasePayoutDataItem extends BaseDataItem {
    source: 'earnings_v1';
    timestamp: string;
}

export interface EngagementPayoutDataItem extends BasePayoutDataItem {
    payload: {earnings: number};
}

export interface SacPayoutDataItem extends BasePayoutDataItem {
    payload: {sac_earnings: number};
}

export interface AccoladeDataItem {
    timestamp: string;
    payload: {
        accolades: {[key: string]: number};
        percentile: number;
        versions: number[];
    };
}

export interface AccoladeResponseDataItem extends BaseDataItem {
    source: 'accolades_v1';
    timestamp: string;
    creative_account_id: string;
    payload: string;
}

export interface AccoladeMetadata {
    accolades: {
        [key: string]: {
            name: string;
            guid: string;
            description: string;
            weight: string;
        };
    };
    calibration: string;
}

export interface AccoladeMetadataResponse {
    metadata: {
        _accolades: {
            name: string;
            guid: string;
            description: string;
            weight: string;
        }[];
        dynamicXp: {
            calibrationPhase: string;
        };
    };
}

export interface PerformanceMetricDataItem {
    timestamp: string;
    payload: {
        [key: string]: {
            [key: string]: number;
        };
    };
}

export interface PerformanceMetricResponseDataItem extends BaseDataItem {
    source: PerfSource;
    timestamp: string;
    creative_account_id: string;
    payload: string;
}

export interface PlaytimeResponseDataItem extends BaseDataItem {
    source: 'playtime_v1';
    timestamp: string;
    creative_account_id: string;
    payload: string;
}

export interface PlaytimeDataItem {
    payload: {
        as_of: string;
        per_version: {
            [key: string]: {
                [key: string]: number[];
            };
        };
    };
    creative_island_code: string;
}
